import './global.scss';
import React, {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/browser';
import {
  AppLauncherContent,
  AppLauncherTrigger,
  Routes,
  Route,
  SkipToContent,
  SkipToContentItem,
  VirtuallyHidden,
  OfflineIndicator,
  useResponsiveValue,
  SIDEBAR_BREAKPOINTS,
  Sidebar,
  SidebarPrioritySection,
  LoadingIndicator,
} from '@monash/portal-react';
import {
  AuthContext,
  ChatBotContext,
  ImpersonationContext,
  useSessionStorage,
  useUserMenuAvatar,
  useUserMenuProfile,
  UserInitialsContent,
  UserInitialsTrigger,
  useAppLauncherItems,
} from '@monash/portal-frontend-common';
import Nav from './components/ui/nav/Nav';
import Main from './components/ui/main/Main';
import ErrorScreen from './components/utilities/error/ErrorScreen';
import Notification from './components/ui/notification/Notification';
import SearchResult from './components/pages/search/SearchResult';
import { DataContext } from './components/providers/data-provider/DataProvider';
import PortalPreferencesErrorModal from './components/ui/portal-preferences-error-modal/PortalPreferencesErrorModal';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import GenericError from './components/utilities/error/GenericError';
import SearchButton from './components/ui/nav/top-nav-items/search-button/SearchButton';
import Themer from './components/themer/Themer';
import getUserInitialsCustomSections from './components/utilities/user-initials-custom-sections/get-user-initials-custom-sections';
import { useAnnouncements } from './utils/use-announcements/use-announcements';
import { useParkingDataInterval } from '@/hooks/use-parking-data-interval';
import c from './app.module.scss';

const Onboarding = lazy(() =>
  import('./components/pages/onboarding/Onboarding')
);

const App = () => {
  const { currentUser } = useContext(ImpersonationContext);
  const { userDoc } = useContext(AuthContext);
  const { errors, avatar, profileData } = useContext(DataContext);

  const { ChatBotButton } = useContext(ChatBotContext);

  const [isOffline, setIsOffline] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(
    userDoc?.history?.onboardingProgress === undefined
  );

  const size = useResponsiveValue(SIDEBAR_BREAKPOINTS);

  const [showEncumbrances] = useSessionStorage('mock:showEncumbrances');

  useAnnouncements();

  useEffect(() => {
    Sentry.setUser({ id: currentUser?.uid });

    window.addEventListener('offline', () => {
      setIsOffline(true);
    });
    window.addEventListener('online', () => {
      setIsOffline(false);
    });
  }, []);

  // Themer
  const [showThemer, setShowThemer] = useState(false);

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarTriggerRef = useRef();

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen((prevSidebarOpen) => !prevSidebarOpen);
  };

  useParkingDataInterval();

  // Sidebar - app launcher
  const APP_LAUNCHER_TITLE = 'Quick links';
  const [appLauncherOpen, setAppLauncherOpen] = useState(false);
  const { items: appLauncherItems } = useAppLauncherItems();

  // Sidebar - user menu
  const USER_MENU_TITLE = 'User menu';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { avatarSrc, avatarAlt } = useUserMenuAvatar({
    avatarData: avatar,
    getData: false,
  });
  const { name, email, id, pronouns } = useUserMenuProfile({
    profileData,
    getData: false,
  });

  if (!currentUser?.successfulIdentityResponse) {
    return (
      <ErrorScreen
        heading="Access Denied"
        message="Sorry, you don't have permission to access this application"
        illustration="brokenFlowers"
      />
    );
  }

  const [fadeOut, setFadeOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // State to control visibility

  useEffect(() => {
    // Set a timeout to trigger the fade out after the component mounts
    const timer = setTimeout(() => {
      setFadeOut(true);
    }, 200);

    // Set another timeout to remove the layer after the fade-out transition
    const removeLayerTimer = setTimeout(() => {
      setIsVisible(false);
    }, 500); // Match this duration with the CSS transition duration

    // Cleanup the timers on unmount
    return () => {
      clearTimeout(timer);
      clearTimeout(removeLayerTimer);
    };
  }, []);

  const onboarding = (
    <Suspense fallback={<LoadingIndicator color="white" />}>
      <div className="app" id="appWrapper">
        <Onboarding updateOnboarding={setIsOnboarded} />
      </div>
    </Suspense>
  );

  const nonOnboarding = (
    <div className="app">
      {isVisible && (
        <div className={`${c.transitionLayer} ${fadeOut ? c.fadeOut : ''}`} />
      )}
      <SkipToContent>
        <SkipToContentItem text="Top navigation" skipTo="#topNav" />
        <SkipToContentItem text="Content" skipTo="#contentContainer" />
      </SkipToContent>
      {showThemer && <Themer {...{ showThemer, setShowThemer }} />}

      {size === 'XS' && (
        <>
          {/* Sidebar controlled menus */}
          <AppLauncherContent
            shown={appLauncherOpen}
            setShown={setAppLauncherOpen}
            items={appLauncherItems}
            triggerRef={sidebarTriggerRef}
            title={APP_LAUNCHER_TITLE}
          />
          <UserInitialsContent
            shown={userMenuOpen}
            setShown={setUserMenuOpen}
            triggerRef={sidebarTriggerRef}
            title={USER_MENU_TITLE}
            avatarSrc={avatarSrc}
            avatarAlt={avatarAlt}
            name={name}
            email={email}
            id={id}
            pronouns={pronouns}
            appVersion={import.meta.env.VITE_VERSION}
          >
            {getUserInitialsCustomSections({ setShowThemer })}
          </UserInitialsContent>
          <Sidebar
            ifMenu={sidebarOpen}
            size={size}
            closeMenu={closeSidebar}
            toggleMenu={toggleSidebar}
            mode="card"
          >
            <SidebarPrioritySection>
              <SearchButton
                isInSidebar
                onClick={() => {
                  closeSidebar();
                  sidebarTriggerRef.current?.focus();
                }}
              />
              <AppLauncherTrigger
                isInSidebar
                title={APP_LAUNCHER_TITLE}
                onClick={() => {
                  setAppLauncherOpen(true);
                  closeSidebar();
                }}
              />
              <ChatBotButton
                onClick={closeSidebar}
                data-tracking-event="topnav-chatbot"
                isInSidebar
              />
              <UserInitialsTrigger
                isInSidebar
                onClick={() => {
                  setUserMenuOpen(true);
                  closeSidebar();
                }}
                avatarSrc={avatarSrc}
              />
            </SidebarPrioritySection>
          </Sidebar>
        </>
      )}
      <Nav
        setSidebarOpen={setSidebarOpen}
        sidebarTriggerRef={sidebarTriggerRef}
        showThemer={showThemer}
        setShowThemer={setShowThemer}
      />
      <div className="app-content-wrapper">
        <Routes matchFirstOnly>
          <Route to="/search" routeProps>
            <VirtuallyHidden element="h1" text="Search" />
            <SearchResult />
          </Route>

          <Route to="/preview">
            <Main />
          </Route>

          <Route to="/">
            <Main />
          </Route>
        </Routes>

        {/* The modal animates twice when using the `open` prop conditionally */}
        {Boolean(errors.portalPreferences) && (
          <PortalPreferencesErrorModal open={true} />
        )}
        {(currentUser.hasEncumbrances || showEncumbrances) && (
          <Notification type="encumbrance" />
        )}
      </div>
      {isOffline && <OfflineIndicator className={c.offlineIndicator} />}
    </div>
  );

  return (
    <SentryErrorBoundary fallback={<GenericError />}>
      {!isOnboarded ? onboarding : nonOnboarding}
    </SentryErrorBoundary>
  );
};

export default App;
